'use client';

import { Loader } from '@/components/common/loader';
import { Button, buttonVariants } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { MultidietPickerForm } from '@/features/multidiet-picker/components/multidiet-picker-form';
import { MultidietPickerFormMobile } from '@/features/multidiet-picker/components/multidiet-picker-form-mobile';
import { MultidietVariants } from '@/features/multidiet-picker/components/multidiet-variants';
import { useMultidietPickerForm } from '@/features/multidiet-picker/hooks/useMultidietPickerForm';
import useMultidietSetupStore from '@/features/multidiet-picker/store/multidiet-picker.store';
import { MultidietPopupType } from '@/features/multidiet-picker/types/multidiet-popup.type';
import type { MultidietPickerType } from '@/features/static-builder/types/multidiet-picker.type';
import { interpretColors } from '@/lib/helpers/typography';
import { useMobile } from '@/lib/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { IconArrowRight } from '@tabler/icons-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

export const MultidietPicker = ({
  data,
  layout = 'page',
}: {
  data: MultidietPickerType | MultidietPopupType;
  layout: 'popup' | 'page';
}) => {
  const t = useTranslations();
  const { form, onSubmit } = useMultidietPickerForm();
  const { isMobileExtendedPlus } = useMobile();
  const { multidietSetup, multidietSetupLabels } = useMultidietSetupStore();

  return (
    <section className={cn('py-20', layout === 'popup' && 'py-0')}>
      <div className='container'>
        <div
          className={cn(
            'flex flex-col gap-4 items-center',
            layout === 'page' &&
              'lg:flex-row lg:px-16 lg:py-14 lg:gap-12 bg-brand-grey2 rounded-xl',
            layout === 'popup' && 'lg:p-6',
          )}
        >
          <header
            className={cn(
              ' p-4 pb-0 shrink-0',
              layout === 'popup' && 'lg:w-full pt-8 lg:pt-0',
              layout === 'page' && ' pt-11 lg:p-0 lg:w-[300px] xl:w-[370px]',
            )}
          >
            <h2
              className={cn(
                ' max-md:leading-10  font-medium text-center',
                layout === 'page' && 'text-3.5xl md:text-5xl lg:text-left',
                layout === 'popup' && 'text-3.5xl',
              )}
              dangerouslySetInnerHTML={{
                __html: interpretColors(data.title || ''),
              }}
            />
            <p
              className={cn(
                'text-base font-medium md:font-regular text-center  my-5  ',
                layout === 'page' &&
                  'lg:pb-6 lg:text-left lg:my-12 my-5 md:text-2xl font-regular ',
                data.buttonUrl && 'lg:border-b border-brand-green1/30',
              )}
            >
              {data.description}
            </p>

            {data.buttonUrl && (
              <p
                className={cn(
                  'hidden lg:block',
                  layout === 'popup' && 'lg:hidden',
                )}
              >
                <Link
                  href={data.buttonUrl}
                  className={cn(
                    buttonVariants({ variant: 'default', size: 'lg' }),
                    'h-14 font-medium',
                  )}
                >
                  {t('multidiet-picker.see-more')}
                  <IconArrowRight stroke={1.25} className='ml-2' />
                </Link>
              </p>
            )}
          </header>
          <div className='flex flex-col gap-5 pb-11 lg:p-0 w-full max-lg:overflow-hidden'>
            {multidietSetupLabels && multidietSetup ? (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div
                    className={cn(
                      layout === 'popup' ? 'mb-5' : 'mb-8 xl:mb-14',
                    )}
                  >
                    {!isMobileExtendedPlus && multidietSetup ? (
                      <MultidietPickerForm
                        layout={layout}
                        form={form}
                        multidietSetup={multidietSetup}
                      />
                    ) : (
                      <MultidietPickerFormMobile layout={layout} form={form} />
                    )}
                  </div>
                  {multidietSetupLabels && (
                    <MultidietVariants
                      layout={layout}
                      multidietSetupLabels={multidietSetupLabels}
                    />
                  )}
                </form>
              </Form>
            ) : (
              <Loader />
            )}

            <div className='px-4 lg:px-0'>
              <p className='text-sm text-center'>
                {t('multidiet-picker.info')}
              </p>
              {data.buttonUrl && (
                <p className='text-center mt-8'>
                  <Link
                    href={data.buttonUrl}
                    className='flex items-center justify-center underline underline-offset-4 hover:no-underline md:hidden'
                  >
                    {t('multidiet-picker.learn-more')}
                    <IconArrowRight stroke={1.25} className='ml-2 w-5' />
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
